import { cn } from "@/lib/utils";
import ChatAvatar from "./ChatAvatar";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import "./chat-style.css";
import remarkCitations from "./remarkCitation";
import { useGetAttachments } from "../contract/useGetAttachments";
import { getAttachment } from "../contract/Attachments";

function ChatBubbleLoader() {
  return (
    <div className="flex flex-row gap-2 items-center h-4">
      <div className="h-2 w-2 rounded-full bg-muted-foreground/85 animate-pulse delay-300" />
      <div className="h-2 w-2 rounded-full bg-muted-foreground/85 animate-pulse delay-600" />
      <div className="h-2 w-2 rounded-full bg-muted-foreground/85 animate-pulse delay-900" />
    </div>
  );
}

function ChatCitation({ node, attachments, searchType, proposalId, ...props }) {
  const source = node.properties?.source?.replace(/\.[^/.]+$/, "") || "";
  const value = node.children?.[0]?.value || "";
  const attachs = attachments?.map((attachment) =>
    getAttachment(attachment, searchType)
  );
  function scrollToAttachment() {
    if (source === proposalId) {
      return;
    }
    const element = document.getElementById(source);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      element.classList.add("bg-muted", "transition", "ease-in-out");

      // Optionally, remove the highlight after some time
      setTimeout(() => {
        element.classList.remove("bg-muted", "transition", "ease-in-out");
      }, 1000); // 2 seconds, adjust as needed
    }
  }
  const attachmentName =
    source === proposalId
      ? "Opportunity Details"
      : attachs?.find((a) => a.attachmentId === source)?.name;
  return (
    <HoverCard openDelay={200}>
      <HoverCardTrigger asChild>
        <span
          className="font-medium text-muted-foreground cursor-pointer"
          onClick={scrollToAttachment}
        >
          {value}
        </span>
      </HoverCardTrigger>
      <HoverCardContent>
        <span className="text-muted-foreground">Source: </span>
        {attachmentName ? (
          <span
            className="hover:cursor-pointer hover:underline"
            onClick={scrollToAttachment}
          >
            {attachmentName}
          </span>
        ) : (
          source
        )}
      </HoverCardContent>
    </HoverCard>
  );
}

function ChatTable({ children }) {
  return <div className="w-full overflow-x-scroll">{children}</div>;
}

function ChatBubble({ message, searchType, proposalId }) {
  const { data: attachments } = useGetAttachments(searchType, proposalId);
  const regex =
    /Given the context: (.*?) and the question: (.*?), please answer the question\./s;

  const match = message.content.match(regex);

  const question = match ? match[2] : message.content;
  return (
    <div className="flex flex-col items-left">
      {message.role !== "user" && <ChatAvatar role={message.role} />}
      <div
        className={cn(
          "markdown flex max-w-full flex-col gap-2 rounded-lg px-3 py-2 text-sm",
          message.role === "user"
            ? "ml-auto bg-primary text-primary-foreground"
            : "mr-auto bg-muted/70"
        )}
      >
        {question === "Thinking..." ? (
          <ChatBubbleLoader />
        ) : (
          <Markdown
            remarkPlugins={[remarkGfm, remarkCitations]}
            components={{
              citation: (props) => (
                <ChatCitation
                  {...props}
                  attachments={attachments}
                  searchType={searchType}
                  proposalId={proposalId}
                />
              ),
              // citation: ChatLink
              table: (props) => <ChatTable {...props} />,
            }}
          >
            {question}
          </Markdown>
        )}
      </div>
    </div>
  );
}

export default ChatBubble;
